import { useState } from "react";
import { useAuth } from "@/lib/context/auth-context";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StateRequirements } from "@/pages/admin/types";
import { PieChart, Pie, BarChart, Bar, Cell, Sector, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Filter, Info, PieChart as PieChartIcon, GraduationCap } from "lucide-react";
import { DashboardHeader } from "../components/DashboardHeader";

const CategoryAnalytics = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"pie" | "bar" | "table">("table");
  
  const isStaff = profile?.role === 'staff' || profile?.role === 'admin';

  if (!isStaff) {
    navigate('/analytics');
    return null;
  }

  const { data: categoryData = {
    coursesByCategory: [],
    categoryCourses: {},
    totalCourses: 0
  }, isLoading } = useQuery({
    queryKey: ['categoryAnalytics'],
    queryFn: async () => {
      try {
        const { data: courses, error: coursesError } = await supabase
          .from('courses')
          .select('id, title, state_requirements, event_type, created_at, status, category, subject_area, target_audience');

        if (coursesError) throw new Error(coursesError.message);

        const categoryCounts: { [key: string]: number } = {
          'Positive Behavioral Intervention Strategies': 0,
          'Reading Preparation': 0,
          'Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents': 0,
          'Suicide Prevention': 0,
          'English Learners': 0,
          'American Indian History and Culture': 0,
          'Cultural Competency': 0,
          'Other': 0
        };

        const categoryCourses: { [key: string]: any[] } = {
          'Positive Behavioral Intervention Strategies': [],
          'Reading Preparation': [],
          'Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents': [],
          'Suicide Prevention': [],
          'English Learners': [],
          'American Indian History and Culture': [],
          'Cultural Competency': [],
          'Other': []
        };

        courses.forEach(course => {
          let categoryFound = false;
          const requirements = course.state_requirements as unknown as StateRequirements;

          if (requirements) {
            try {
              if (requirements.behavioralIntervention) {
                categoryCounts['Positive Behavioral Intervention Strategies']++;
                categoryCourses['Positive Behavioral Intervention Strategies'].push(course);
                categoryFound = true;
              }
              if (requirements.readingPreparation) {
                categoryCounts['Reading Preparation']++;
                categoryCourses['Reading Preparation'].push(course);
                categoryFound = true;
              }
              if (requirements.keyWarningSigns) {
                categoryCounts['Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents']++;
                categoryCourses['Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents'].push(course);
                categoryFound = true;
              }
              if (requirements.suicidePrevention) {
                categoryCounts['Suicide Prevention']++;
                categoryCourses['Suicide Prevention'].push(course);
                categoryFound = true;
              }
              if (requirements.englishLearners) {
                categoryCounts['English Learners']++;
                categoryCourses['English Learners'].push(course);
                categoryFound = true;
              }
              if (requirements.americanIndianHistory) {
                categoryCounts['American Indian History and Culture']++;
                categoryCourses['American Indian History and Culture'].push(course);
                categoryFound = true;
              }
              if (requirements.culturalCompetency?.enabled) {
                categoryCounts['Cultural Competency']++;
                categoryCourses['Cultural Competency'].push(course);
                categoryFound = true;
              }
            } catch (e) {
              console.error("Error processing state requirements:", e);
            }
          }

          if (!categoryFound) {
            categoryCounts['Other']++;
            categoryCourses['Other'].push(course);
          }
        });

        const coursesByCategory = Object.entries(categoryCounts)
          .filter(([_, value]) => value > 0)
          .map(([name, value]) => ({
            name,
            value,
            percent: value / courses.length
          }));

        // Get registrations to calculate completion rate
        const { data: registrations, error: regError } = await supabase
          .from('course_registrations')
          .select('id, course_id, status, user_id');

        if (regError) throw new Error(regError.message);

        // Get certificates to calculate accurate completion rate
        const { data: certificates, error: certError } = await supabase
          .from('certificates')
          .select('id, course_id, user_id');

        if (certError) throw new Error(certError.message);

        console.log("Certificates data:", certificates?.length || 0);

        // Process course data with registrations and certificates
        Object.keys(categoryCourses).forEach(category => {
          categoryCourses[category] = categoryCourses[category].map(course => {
            const courseRegistrations = registrations.filter(reg => reg.course_id === course.id);
            const courseCertificates = certificates.filter(cert => cert.course_id === course.id);
            
            return {
              ...course,
              registrations: courseRegistrations.length,
              completions: courseCertificates.length, // Use certificates as the measure of completion
            };
          });
        });

        return {
          coursesByCategory,
          categoryCourses,
          totalCourses: courses.length
        };
      } catch (error) {
        console.error('Error fetching category analytics:', error);
        toast({
          title: "Error loading analytics",
          description: "Unable to load category analytics data. Please try again later.",
          variant: "destructive",
        });
        return {
          coursesByCategory: [],
          categoryCourses: {},
          totalCourses: 0
        };
      }
    },
  });

  const CATEGORY_COLORS = {
    'Positive Behavioral Intervention Strategies': '#004161',
    'Reading Preparation': '#cfb9a2',
    'Key Warning Signs for Early-Onset Mental Illness in Children and Adolescents': '#0f8f8c',
    'Suicide Prevention': '#b1c3cf',
    'English Learners': '#004161',
    'American Indian History and Culture': '#cfb9a2',
    'Cultural Competency': '#0f8f8c',
    'Other': '#f1eceb'
  };

  const getCategoryColor = (name) => {
    return CATEGORY_COLORS[name] || '#004161';
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  const CustomPieTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded shadow-lg border border-gray-200">
          <p className="font-bold text-primary font-title">{`${payload[0].name}`}</p>
          <p className="text-muted-foreground">{`Courses: ${payload[0].value}`}</p>
          <p className="text-muted-foreground">{`Percentage: ${(payload[0].payload.percent * 100).toFixed(1)}%`}</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.1;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const textAnchor = x > cx ? 'start' : 'end';
    
    return (
      <text 
        x={x} 
        y={y} 
        fill={getCategoryColor(name)}
        textAnchor={textAnchor} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight={index === activeIndex ? 'bold' : 'normal'}
        fontFamily="'Proxima Nova', 'Inter', sans-serif"
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="w-full py-4 px-2 space-y-4">
      <DashboardHeader
        title="Category Analytics"
        subtitle="Analyze course distribution and completion by category"
      />
      <div className="container mx-auto px-6 space-y-6 max-w-7xl">
        <div className="flex flex-row items-center justify-between mb-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/analytics')}
                className="mr-2"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={viewMode === "pie" ? "default" : "outline"} 
              size="sm" 
              onClick={() => setViewMode("pie")}
              className="font-subtitle"
            >
              <PieChartIcon className="h-4 w-4 mr-2" />
              Pie Chart
            </Button>
            <Button 
              variant={viewMode === "bar" ? "default" : "outline"} 
              size="sm" 
              onClick={() => setViewMode("bar")}
              className="font-subtitle"
            >
              <Filter className="h-4 w-4 mr-2" />
              Bar Chart
            </Button>
            <Button 
              variant={viewMode === "table" ? "default" : "outline"} 
              size="sm" 
              onClick={() => setViewMode("table")}
              className="font-subtitle"
            >
              <Info className="h-4 w-4 mr-2" />
              Detailed Table
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="font-title text-primary">Course Distribution by State Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              {viewMode === "table" ? (
                <div className="overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-section text-primary">Category</TableHead>
                        <TableHead className="font-section text-primary">Number of Courses</TableHead>
                        <TableHead className="font-section text-primary">Percentage</TableHead>
                        <TableHead className="font-section text-primary">State Minimum Status</TableHead>
                        <TableHead className="font-section text-primary">Completion Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryData.coursesByCategory.map((category) => {
                        const coursesInCategory = categoryData.categoryCourses[category.name] || [];
                        
                        // Calculate completion rate based on certificates vs. registrations
                        const totalRegistrations = coursesInCategory.reduce((sum, course) => sum + (course.registrations || 0), 0);
                        const totalCompletions = coursesInCategory.reduce((sum, course) => sum + (course.completions || 0), 0);
                        
                        // Calculate completion rate, avoiding division by zero
                        let completionRate = 0;
                        if (totalRegistrations > 0) {
                          completionRate = (totalCompletions / totalRegistrations) * 100;
                        }
                        
                        return (
                          <TableRow key={category.name}>
                            <TableCell className="font-medium font-body">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: getCategoryColor(category.name) }}
                                />
                                {category.name}
                              </div>
                            </TableCell>
                            <TableCell className="font-body">{category.value}</TableCell>
                            <TableCell className="font-body">{(category.percent * 100).toFixed(1)}%</TableCell>
                            <TableCell>
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-section bg-accent-subtle text-primary">
                                Meeting Requirements
                              </span>
                            </TableCell>
                            <TableCell className="font-body">{completionRate.toFixed(1)}%</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : viewMode === "bar" ? (
                <div className="h-[500px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={categoryData.coursesByCategory}
                      layout="vertical"
                      margin={{
                        top: 5,
                        right: 30,
                        left: 150,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        type="number"
                        tick={{
                          fontFamily: "'Proxima Nova', sans-serif",
                          fill: "#004161"
                        }} 
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name"
                        tick={{ 
                          fill: "#004161", 
                          fontSize: 12,
                          fontFamily: "'Proxima Nova', sans-serif"
                        }}
                        width={150}
                      />
                      <Tooltip 
                        formatter={(value, name, props) => [`${value} courses`, props.payload.name]}
                        contentStyle={{
                          fontFamily: "'Proxima Nova', sans-serif",
                          borderColor: "#cfb9a2",
                          backgroundColor: "#ffffff",
                          color: "#004161"
                        }}
                      />
                      <Legend 
                        wrapperStyle={{
                          fontFamily: "'Proxima Nova', sans-serif",
                          color: "#004161"
                        }}
                      />
                      <Bar dataKey="value" name="Number of Courses">
                        {categoryData.coursesByCategory.map((entry) => (
                          <Cell 
                            key={`cell-${entry.name}`} 
                            fill={getCategoryColor(entry.name)}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[500px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData.coursesByCategory}
                        cx="50%"
                        cy="50%"
                        labelLine={true}
                        label={renderCustomizedLabel}
                        outerRadius={180}
                        fill="#8884d8"
                        dataKey="value"
                        onMouseEnter={onPieEnter}
                        onMouseLeave={onPieLeave}
                        activeIndex={activeIndex}
                        activeShape={(props) => {
                          const RADIAN = Math.PI / 180;
                          const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, name, value } = props;
                          const sin = Math.sin(-RADIAN * midAngle);
                          const cos = Math.cos(-RADIAN * midAngle);
                          const mx = cx + (outerRadius + 30) * cos;
                          const my = cy + (outerRadius + 30) * sin;
                          const ex = mx + (cos >= 0 ? 1 : -1) * 22;
                          const ey = my;
                          const textAnchor = cos >= 0 ? 'start' : 'end';
                          const percent = value / categoryData.totalCourses;
                          
                          return (
                            <g>
                              <text x={cx} y={cy} dy={8} textAnchor="middle" fill="#004161" fontSize={16} fontWeight="bold" fontFamily="'Proxima Nova', sans-serif">
                                {name}
                              </text>
                              <Sector
                                cx={cx}
                                cy={cy}
                                innerRadius={innerRadius}
                                outerRadius={outerRadius + 10}
                                startAngle={startAngle}
                                endAngle={endAngle}
                                fill={fill}
                                opacity={0.8}
                              />
                              <path d={`M${cx},${cy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" strokeWidth={2}/>
                              <circle cx={ex} cy={ey} r={4} fill={fill} stroke="none"/>
                              <text 
                                x={ex + (cos >= 0 ? 1 : -1) * 12} 
                                y={ey} 
                                textAnchor={textAnchor} 
                                fill="#004161" 
                                fontSize={14}
                                fontFamily="'Proxima Nova', sans-serif"
                              >
                                {`${value} courses (${(percent * 100).toFixed(1)}%)`}
                              </text>
                            </g>
                          );
                        }}
                      >
                        {categoryData.coursesByCategory.map((entry) => (
                          <Cell 
                            key={`cell-${entry.name}`} 
                            fill={getCategoryColor(entry.name)}
                            strokeWidth={activeIndex === categoryData.coursesByCategory.findIndex(item => item.name === entry.name) ? 2 : 0}
                            stroke="#fff"
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend 
                        layout="horizontal" 
                        verticalAlign="bottom" 
                        align="center"
                        wrapperStyle={{
                          fontFamily: "'Proxima Nova', sans-serif",
                        }}
                        formatter={(value, entry, index) => (
                          <span style={{ 
                            color: getCategoryColor(value), 
                            fontWeight: activeIndex === index ? 'bold' : 'normal',
                            fontFamily: "'Proxima Nova', sans-serif",
                          }}>
                            {value}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-title text-primary">Category Insights</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-sm font-section mb-2 text-primary">Distribution Overview</h3>
                <div className="w-full h-2 bg-accent-subtle rounded-full overflow-hidden relative">
                  {categoryData.coursesByCategory.map((category, index) => {
                    const previousWidths = categoryData.coursesByCategory
                      .slice(0, index)
                      .reduce((sum, c) => sum + c.percent, 0);
                    
                    return (
                      <div
                        key={category.name}
                        className="h-full absolute"
                        style={{
                          width: `${category.percent * 100}%`,
                          left: `${previousWidths * 100}%`,
                          backgroundColor: getCategoryColor(category.name)
                        }}
                        title={`${category.name}: ${(category.percent * 100).toFixed(1)}%`}
                      />
                    );
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-section mb-2 text-primary">Category Statistics</h3>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground font-caption">Total Categories</dt>
                    <dd className="text-2xl font-bold text-primary font-title">{categoryData.coursesByCategory.length}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground font-caption">Total Courses</dt>
                    <dd className="text-2xl font-bold text-primary font-title">{categoryData.totalCourses}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <h3 className="text-sm font-section mb-2 text-primary">Most Popular Categories</h3>
                <div className="space-y-2">
                  {[...categoryData.coursesByCategory]
                    .sort((a, b) => b.value - a.value)
                    .slice(0, 3)
                    .map((category, index) => (
                      <div key={category.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: getCategoryColor(category.name) }}
                          />
                          <span className="text-sm font-medium font-body">{category.name}</span>
                        </div>
                        <span className="text-sm text-muted-foreground font-caption">
                          {category.value} courses
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {viewMode === "table" && (
          <div className="mt-6 grid grid-cols-1 gap-6">
            {Object.keys(categoryData.categoryCourses)
              .filter(key => categoryData.categoryCourses[key].length > 0)
              .map(category => (
                <Card key={category}>
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: getCategoryColor(category) }}
                      />
                      <CardTitle className="text-lg font-title text-primary">
                        {category} ({categoryData.categoryCourses[category].length} courses)
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="font-section text-primary">Course Title</TableHead>
                          <TableHead className="font-section text-primary">Event Type</TableHead>
                          <TableHead className="font-section text-primary">Subject Area</TableHead>
                          <TableHead className="font-section text-primary">Target Audience</TableHead>
                          <TableHead className="font-section text-primary">Registrations</TableHead>
                          <TableHead className="font-section text-primary">Completions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {categoryData.categoryCourses[category].map(course => (
                          <TableRow key={course.id}>
                            <TableCell className="font-medium font-body">{course.title}</TableCell>
                            <TableCell className="font-body">
                              {course.event_type === 'live' ? 'Live Event' : 'Self-Paced'}
                            </TableCell>
                            <TableCell className="font-body">{course.subject_area}</TableCell>
                            <TableCell className="font-body">{course.target_audience}</TableCell>
                            <TableCell className="font-body">{course.registrations || 0}</TableCell>
                            <TableCell className="font-body">{course.completions || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryAnalytics;
